import { motion } from 'framer-motion'
import { Card } from '@/components/ui/card'
import { Markdown } from './markdown'
import { cn } from '@/lib/utils'
import { getRoleDetails } from '@/utils/chat-messages-style-config'
import { useTranslation } from 'react-i18next'
import { formatTimeAgo } from '@/utils/format-time-ago'
import { useAuthStore } from '@/stores/authStore'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { tokens } from '@/locales/translations/tokens'
import { forwardRef, useCallback, useEffect, useState } from 'react'
import MessageFeedback from './feedback/message-feedback'
import { addFeedbackToMessage, removeFeedbackFromMessage } from '@/api/workstation'
import { ThanksForFeedbackMessage } from './feedback/thanks-for-feedback-message'
import { useGlobalSourceManagementStore } from '@/stores/globalSourceManagementStore'
import type { FeedbackType } from '@/api/types/workstation.types'
import { UIMessage } from 'ai'
import { getMessageText, getMessageTimestamp } from '@/features/workstation/utils/message-utils'

interface MessageProps {
  message: UIMessage
  isLoading: boolean
}

export const Message = forwardRef<HTMLDivElement, MessageProps>(({
  message,
  isLoading,
}, ref) => {
  const { t, i18n } = useTranslation();
  const formattedCreateTime = formatTimeAgo(new Date(getMessageTimestamp(message)), i18n.language);
  const user = useAuthStore((state) => state.auth.user)
  const [showThanksForFeedback, setShowThanksForFeedback] = useState<boolean>(false);
  const THANKS_MESSAGE_EXPIRATION_TIME = 3000;
  const globalSourceContext = useGlobalSourceManagementStore();
  const messageText = getMessageText(message);

  useEffect(() => {
    if(showThanksForFeedback) {
      const timeout = setTimeout(() => {
        setShowThanksForFeedback(false);
      }, THANKS_MESSAGE_EXPIRATION_TIME);
      return () => clearTimeout(timeout);
    }
  }, [showThanksForFeedback])

  const handleMessageFeedback = useCallback(
    async (
      type: FeedbackType, 
      messageId: string, 
      operation: "add" | "remove", 
      reason?: string,
      suggestedReasons: string[] = []
    ) => {
      switch (operation) {
        case "add":
          await addFeedbackToMessage({
            feedbackType: type,
            messageId,
            reason,
            suggestedReasons,
          })
          break;

        case "remove": {
          await removeFeedbackFromMessage(messageId);
          break;
        }
      }
  }, []);

  const roleStyleConfig = getRoleDetails(message.role, user?.user_metadata.avatar_url);

  return (
    <motion.div className='p-3.5' ref={ref}>
      <div className={cn(
        'flex w-full gap-4',
        message.role === "assistant" ? "" : "flex-row-reverse",
      )}>
        <Avatar className="h-8 w-8 mt-1">
          {roleStyleConfig.icon}
          <AvatarFallback className="bg-blue-100 text-blue-700">
            {roleStyleConfig.fallbackIcon}
          </AvatarFallback>
        </Avatar>
        <div className='max-w-[80%] flex flex-col relative group'>
          <Card className={
            `flex full border-border 
            ${roleStyleConfig.bubbleBg} ${roleStyleConfig.bubbleText} ${roleStyleConfig.bubbleRounded}`
          }>
            <div className='flex-1 space-y-1 rounded-lg p-4'>
              <div className='prose prose-sm dark:prose-invert max-w-none'>
                <Markdown
                  onActions={{
                    ATag: (sourceId, startTime) => {
                      if(sourceId) {
                        globalSourceContext.setActiveSource(message.id, sourceId, Number(startTime));
                      }
                    }
                  }}
                >{messageText}</Markdown>
              </div>
              <div className='flex flex-row items-baseline'>
                <div className={
                  cn(
                  'flex items-center text-sm mr-1 text-primary/70',
                  )
                }>
                  {message.role === 'user' ? t(tokens.workstation.messages.me) : 'Alan'},
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {formattedCreateTime}
                </div>
                </div>
            </div>
          </Card>
          <section className='flex flex-row'>
          {message.role === "assistant" && (
            <div>
              <ThanksForFeedbackMessage 
                visible={showThanksForFeedback} 
                message={t(tokens.workstation.feedbackModal.thankYouFeedback)}
              />
              <MessageFeedback 
                messageId={message.id} 
                visible={!showThanksForFeedback && !isLoading}
                onFeedbackSubmit={handleMessageFeedback} 
                setShowThanksForFeedback={setShowThanksForFeedback}
                className={cn(
                  "opacity-0 translate-y-1 transition-all duration-300 ease-in-out",
                  "group-hover:opacity-100 group-hover:translate-y-0"
                )}
              />
            </div>
          )}
          </section>
        </div>
      </div>
    </motion.div>
  )

}) 
